import { IndexList } from '@/models/IndexList';
import axiosBaseQuery from '@api/axiosBaseQuery';
import { PricerModes } from '@pages/Pricers/enums';
import { createApi } from '@reduxjs/toolkit/query/react';

import { BankEntity } from '../../../../models/BankEntity';
import { BusinessDayConvention } from '../../../../models/BusinessDayConvention';
import { Calendar } from '../../../../models/Calendar';
import { CdsCurves } from '../../../../models/CdsCurves';
import { CommercialTypes } from '../../../../models/CommercialTypes';
import { CommodityIndices } from '../../../../models/CommodityIndices';
import { CompanyEntityFull } from '../../../../models/CompanyEntityFull';
import { Currency } from '../../../../models/Currency';
import { CurrencyConvention } from '../../../../models/CurrencyConvention';
import { DayCountType } from '../../../../models/DayCountType';
import { HedgedItem } from '../../../../models/HedgedItem';
import { HedgedRisk } from '../../../../models/HedgedRisk';
import { HedgingInstrument } from '../../../../models/HedgingInstrument';
import { InflationIndices } from '../../../../models/InflationIndices';
import { LegType } from '../../../../models/LegType';
import { PayAcceptance } from '../../../../models/PayAcceptance';
import { PayFrequency } from '../../../../models/PayFrequency';
import { PaymentType } from '../../../../models/PaymentType';
import { ResetCalculation } from '../../../../models/ResetCalculation';
import { TransactionType } from '../../../../models/TransactionType';

import {
  GetBankEntitiesSuccessPayload,
  GetBusinessDayConventionsSuccessPayload,
  GetCalendarPMCSuccessPayload,
  GetCdsCurvesSuccessPayload,
  GetClosestTradeValuationDateSuccessPayload,
  GetCommercialTypesSuccessPayload,
  GetCommodityIndicesSuccessPayload,
  GetCompanyEntitiesSuccessPayload,
  GetCurrenciesSuccessPayload,
  GetCurrencyConventionsSuccessPayload,
  GetDayCountTypesSuccessPayload,
  GetHedgedItemsSuccessPayload,
  GetHedgedRisksSuccessPayload,
  GetHedgingInstrumentsSuccessPayload,
  GetIndexListSuccessPayload,
  GetInflationIndicesSuccessPayload,
  GetLegTypesSuccessPayload,
  GetPayAcceptanceTypesSuccessPayload,
  GetPayFrequenciesSuccessPayload,
  GetPaymentTypesSuccessPayload,
  GetResetCalculationBasesSuccessPayload,
  GetTransactionTypesSuccessPayload,
} from './actions';

export const pricerPreLoadApi = createApi({
  reducerPath: 'pricerPreLoadApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'ClosestTradeValuationDate',
    'IndexList',
    'Currencies',
    'BusinessDayConventions',
    'DayCountTypes',
    'LegTypes',
    'ResetCalculationBases',
    'PayFrequencies',
    'PaymentTypes',
    'PayAcceptanceTypes',
    'HedgedItems',
    'HedgedRisks',
    'CompanyEntities',
    'BankEntities',
    'TransactionTypes',
    'HedgingInstruments',
    'CurrencyConventions',
    'CalendarPMC',
    'InflationIndices',
    'CommodityIndices',
    'CdsCurves',
    'CommercialTypes',
  ],
  endpoints: (builder) => ({
    fetchClosestTradeValuationDate: builder.query<string, void>({
      query: () => ({
        url: '/v2/hedging/closest-trade-valuation-date/',
      }),
      transformResponse: (
        response: GetClosestTradeValuationDateSuccessPayload,
      ) => {
        return response.data ?? '';
      },
      providesTags: ['ClosestTradeValuationDate'],
      keepUnusedDataFor: 0,
    }),
    fetchIndexList: builder.query<IndexList[], void>({
      query: () => ({
        url: '/v2/market-data/index-list/',
      }),
      transformResponse: (response: GetIndexListSuccessPayload) => {
        return response.data ?? [];
      },
      providesTags: ['IndexList'],
      keepUnusedDataFor: 0,
    }),
    fetchCurrencies: builder.query<Currency[], void>({
      query: () => ({
        url: '/v2/market-data/currencies/',
      }),
      transformResponse: (response: GetCurrenciesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['Currencies'],
      keepUnusedDataFor: 21700,
    }),
    fetchBusinessDayConventions: builder.query<BusinessDayConvention[], void>({
      query: () => ({
        url: '/v2/timekeeping/business-day-conventions/',
      }),
      transformResponse: (
        response: GetBusinessDayConventionsSuccessPayload,
      ) => {
        return response.data;
      },
      providesTags: ['BusinessDayConventions'],
      keepUnusedDataFor: 21700,
    }),
    fetchDayCountTypes: builder.query<DayCountType[], void>({
      query: () => ({
        url: '/v2/timekeeping/day-count-types/',
      }),
      transformResponse: (response: GetDayCountTypesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['DayCountTypes'],
      keepUnusedDataFor: 21700,
    }),
    fetchLegTypes: builder.query<LegType[], void>({
      query: () => ({
        url: '/v2/hedging/leg-types/',
      }),
      transformResponse: (response: GetLegTypesSuccessPayload) => {
        return response.data ?? [];
      },
      providesTags: ['LegTypes'],
      keepUnusedDataFor: 21700,
    }),
    fetchResetCalculationBases: builder.query<ResetCalculation[], void>({
      query: () => ({
        url: '/v2/organizations/reset-calculation-bases/',
      }),
      transformResponse: (response: GetResetCalculationBasesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['ResetCalculationBases'],
      keepUnusedDataFor: 21700,
    }),
    fetchPayFrequencies: builder.query<PayFrequency[], void>({
      query: () => ({
        url: '/v2/constants/pay-frequencies/',
      }),
      transformResponse: (response: GetPayFrequenciesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['PayFrequencies'],
      keepUnusedDataFor: 21700,
    }),
    fetchPaymentTypes: builder.query<PaymentType[], void>({
      query: () => ({
        url: '/v2/constants/payment-types/',
      }),
      transformResponse: (response: GetPaymentTypesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['PaymentTypes'],
      keepUnusedDataFor: 21700,
    }),
    fetchPayAcceptanceTypes: builder.query<PayAcceptance[], void>({
      query: () => ({
        url: '/v2/constants/pay-acceptance-types/',
      }),
      transformResponse: (response: GetPayAcceptanceTypesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['PayAcceptanceTypes'],
      keepUnusedDataFor: 21700,
    }),
    fetchHedgedItems: builder.query<HedgedItem[], void>({
      query: () => ({
        url: '/v2/hedging/hedged-items/',
      }),
      transformResponse: (response: GetHedgedItemsSuccessPayload) => {
        return response.data;
      },
      providesTags: ['HedgedItems'],
      keepUnusedDataFor: 21700,
    }),
    fetchHedgedRisks: builder.query<HedgedRisk[], void>({
      query: () => ({
        url: '/v2/hedging/hedged-risks/',
      }),
      transformResponse: (response: GetHedgedRisksSuccessPayload) => {
        return response.data;
      },
      providesTags: ['HedgedRisks'],
      keepUnusedDataFor: 21700,
    }),
    fetchCompanyEntities: builder.query<CompanyEntityFull[], void>({
      query: () => ({
        url: '/v2/global-filter/company-entities/',
      }),
      transformResponse: (response: GetCompanyEntitiesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['CompanyEntities'],
      keepUnusedDataFor: 0,
    }),
    fetchBankEntities: builder.query<BankEntity[], void>({
      query: () => ({
        url: '/v2/organizations/bank-entities/',
      }),
      transformResponse: (response: GetBankEntitiesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['BankEntities'],
      keepUnusedDataFor: 0,
    }),
    fetchTransactionTypes: builder.query<TransactionType[], void>({
      query: () => ({
        url: '/v2/hedging/transaction-types/',
      }),
      transformResponse: (response: GetTransactionTypesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['TransactionTypes'],
      keepUnusedDataFor: 21700,
    }),
    fetchHedgingInstruments: builder.query<
      HedgingInstrument[],
      { pricerTypeId: PricerModes }
    >({
      query: ({ pricerTypeId }) => ({
        url: `/v2/hedging/pricer-types/${pricerTypeId}/`,
      }),
      transformResponse: (response: GetHedgingInstrumentsSuccessPayload) => {
        return response.data.hedging_instruments || [];
      },
      providesTags: ['HedgingInstruments'],
      keepUnusedDataFor: 21700,
    }),
    fetchCurrencyConventions: builder.query<CurrencyConvention[], void>({
      query: () => ({
        url: '/v2/market-data/currency-conventions/',
      }),
      transformResponse: (response: GetCurrencyConventionsSuccessPayload) => {
        return response.data;
      },
      providesTags: ['CurrencyConventions'],
      keepUnusedDataFor: 21700,
    }),
    fetchCalendarPMC: builder.query<Calendar[], void>({
      query: () => ({
        url: '/v2/reporting/calendars/pmca/',
      }),
      transformResponse: (response: GetCalendarPMCSuccessPayload) => {
        return response.data;
      },
      providesTags: ['CalendarPMC'],
      keepUnusedDataFor: 21700,
    }),
    fetchInflationIndices: builder.query<InflationIndices[], void>({
      query: () => ({
        url: '/v2/market-data/inflation-indices/',
      }),
      transformResponse: (response: GetInflationIndicesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['InflationIndices'],
      keepUnusedDataFor: 21700,
    }),
    fetchCommodityIndices: builder.query<CommodityIndices[], void>({
      query: () => ({
        url: '/v2/market-data/commodity-indices/',
      }),
      transformResponse: (response: GetCommodityIndicesSuccessPayload) => {
        return response.data;
      },
      providesTags: ['CommodityIndices'],
      keepUnusedDataFor: 21700,
    }),
    fetchCdsCurves: builder.query<CdsCurves[], void>({
      query: () => ({
        url: '/v2/company/get-cds-curves/',
      }),
      transformResponse: (response: GetCdsCurvesSuccessPayload) => {
        return response.data || [];
      },
      providesTags: ['CdsCurves'],
      keepUnusedDataFor: 21700,
    }),
    fetchCommercialTypes: builder.query<CommercialTypes[], void>({
      query: () => ({
        url: '/v2/debt/commercial-types/',
      }),
      transformResponse: (response: GetCommercialTypesSuccessPayload) => {
        return response.data || [];
      },
      providesTags: ['CommercialTypes'],
      keepUnusedDataFor: 21700,
    }),
  }),
});

export const {
  useFetchClosestTradeValuationDateQuery,
  useFetchIndexListQuery,
  useFetchCurrenciesQuery,
  useFetchBusinessDayConventionsQuery,
  useFetchDayCountTypesQuery,
  useFetchLegTypesQuery,
  useFetchResetCalculationBasesQuery,
  useFetchPayFrequenciesQuery,
  useFetchPaymentTypesQuery,
  useFetchPayAcceptanceTypesQuery,
  useFetchHedgedItemsQuery,
  useFetchHedgedRisksQuery,
  useFetchCompanyEntitiesQuery,
  useFetchBankEntitiesQuery,
  useFetchTransactionTypesQuery,
  useFetchHedgingInstrumentsQuery,
  useFetchCurrencyConventionsQuery,
  useFetchCalendarPMCQuery,
  useFetchInflationIndicesQuery,
  useFetchCommodityIndicesQuery,
  useFetchCdsCurvesQuery,
  useFetchCommercialTypesQuery,
} = pricerPreLoadApi;

export const selectFetchHedgingInstruments =
  pricerPreLoadApi.endpoints.fetchHedgingInstruments.select;
export const selectFetchIndexList =
  pricerPreLoadApi.endpoints.fetchIndexList.select;
export const selectFetchCurrencies =
  pricerPreLoadApi.endpoints.fetchCurrencies.select;
export const selectFetchCurrencyConventions =
  pricerPreLoadApi.endpoints.fetchCurrencyConventions.select;
export const selectFetchClosestTradeValuationDate =
  pricerPreLoadApi.endpoints.fetchClosestTradeValuationDate.select;
export const selectFetchCompanyEntities =
  pricerPreLoadApi.endpoints.fetchCompanyEntities.select;
